import React, { useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInDays, parseISO } from 'date-fns';
import {
  FileText,
  Clock,
  AlertTriangle,
  Upload,
  List,
  BarChart3,
  Shield,
  X,
  RefreshCw
} from 'lucide-react';
import documentStorageService from '../../services/documentStorageService';
import ComplianceTimeline from './ComplianceTimeline';

const ComplianceDashboard = ({
  documents = [],
  complianceScore = null,
  expiringDocuments = [],
  timelineEvents = [],
  isLoading = false,
  error = null,
  statusMessage = null,
  onDocumentAction,
  onUpload,
  onRenewal,
  onRefresh,
  className = ""
}) => {
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [documentMetadata, setDocumentMetadata] = useState({
    title: '',
    type: '',
    category: '',
    entityType: 'company',
    entityName: '',
    expirationDate: '',
    issueDate: '',
    documentNumber: '',
    issuingAuthority: '',
    notes: ''
  });

  const complianceDocuments = Array.isArray(documents) ? documents : [];

  // Calculate status and urgency
  const getDocumentStatus = (doc) => {
    if (!doc) {
      return { status: 'unknown', urgency: 'low', daysLeft: null };
    }

    if (typeof doc.computedStatus === 'string') {
      return {
        status: doc.computedStatus,
        urgency: doc.urgency || 'low',
        daysLeft: typeof doc.daysUntilExpiration === 'number' ? doc.daysUntilExpiration : null
      };
    }

    if (!doc.expirationDate) {
      return {
        status: doc.status || 'active',
        urgency: doc.urgency || 'low',
        daysLeft: null
      };
    }

    let expirationDate;
    try {
      expirationDate = typeof doc.expirationDate === 'string'
        ? parseISO(doc.expirationDate)
        : doc.expirationDate;
    } catch (parseError) {
      expirationDate = null;
    }

    if (!expirationDate || Number.isNaN(expirationDate.getTime())) {
      return {
        status: doc.status || 'active',
        urgency: doc.urgency || 'low',
        daysLeft: null
      };
    }

    const today = new Date();
    const daysUntilExpiration = differenceInDays(expirationDate, today);

    if (!Number.isFinite(daysUntilExpiration)) {
      return {
        status: doc.status || 'active',
        urgency: doc.urgency || 'low',
        daysLeft: null
      };
    }

    if (doc.status && doc.status !== 'active') {
      return {
        status: doc.status,
        urgency: doc.urgency || (daysUntilExpiration <= 7 ? 'critical' : 'high'),
        daysLeft: daysUntilExpiration
      };
    }

    if (daysUntilExpiration < 0) {
      return { status: 'expired', urgency: 'critical', daysLeft: daysUntilExpiration };
    }
    if (daysUntilExpiration <= 7) {
      return { status: 'expiring_soon', urgency: 'critical', daysLeft: daysUntilExpiration };
    }
    if (daysUntilExpiration <= 30) {
      return { status: 'expiring_soon', urgency: 'high', daysLeft: daysUntilExpiration };
    }
    if (daysUntilExpiration <= 60) {
      return { status: 'expiring_soon', urgency: 'medium', daysLeft: daysUntilExpiration };
    }
    return { status: 'valid', urgency: 'low', daysLeft: daysUntilExpiration };
  };

  // Process documents with calculated status
  const processedDocuments = useMemo(() => {
    return complianceDocuments.map(doc => ({
      ...doc,
      ...getDocumentStatus(doc)
    }));
  }, [complianceDocuments]);

  // Calculate overview metrics
  const overviewMetrics = useMemo(() => {
    const total = processedDocuments.length;
    const expiredFromDocuments = processedDocuments.filter(doc => doc.status === 'expired').length;
    const expiringSoonFromDocuments = processedDocuments.filter(doc => (
      doc.status === 'expiring_soon' || doc.status === 'pending_renewal'
    )).length;
    const valid = processedDocuments.filter(doc => doc.status === 'valid' || doc.status === 'active').length;
    const criticalFromDocuments = processedDocuments.filter(doc => (
      doc.urgency === 'critical' ||
      doc.priority === 'critical' ||
      doc.status === 'expired'
    )).length;

    const expired = processedDocuments.length > 0
      ? expiredFromDocuments
      : expiringDocuments.filter(doc => doc.status === 'expired' || doc.computedStatus === 'expired').length;

    const expiringSoon = processedDocuments.length > 0
      ? expiringSoonFromDocuments
      : expiringDocuments.filter(doc => (
          doc.status === 'expiring_soon' ||
          doc.status === 'pending_renewal' ||
          doc.computedStatus === 'expiring_soon'
        )).length;

    const critical = processedDocuments.length > 0
      ? criticalFromDocuments
      : expiringDocuments.filter(doc => doc.urgency === 'critical' || doc.priority === 'critical').length;

    const backendScore = typeof complianceScore?.score === 'number'
      ? Math.round(complianceScore.score)
      : null;

    const complianceRate = backendScore !== null
      ? backendScore
      : total > 0
        ? Math.round((valid / total) * 100)
        : 0;

    const compliantItems = typeof complianceScore?.compliantItems === 'number'
      ? complianceScore.compliantItems
      : valid;

    const totalItems = typeof complianceScore?.totalItems === 'number'
      ? complianceScore.totalItems
      : total;

    const level = complianceScore?.level
      || (complianceRate >= 90 ? 'excellent'
        : complianceRate >= 75 ? 'good'
        : complianceRate >= 60 ? 'fair'
        : 'poor');

    return {
      total,
      expired,
      expiringSoon,
      valid,
      critical,
      complianceRate: Math.max(0, Math.min(100, complianceRate)),
      compliantItems,
      totalItems,
      level
    };
  }, [processedDocuments, complianceScore]);


  const handleAction = (action, document) => {
    if (onDocumentAction) {
      onDocumentAction(action, document);
    }
    switch (action) {
      case 'renew':
        if (onRenewal) onRenewal(document);
        break;
      case 'upload':
        setShowUploadModal(true);
        if (onUpload) onUpload(document);
        break;
      case 'view':
        if (!onDocumentAction) {
          console.log('View document:', document);
        }
        break;
      case 'download':
        if (!onDocumentAction) {
          handleDownloadDocument(document);
        }
        break;
      case 'scan':
        handleStartScan();
        break;
      default:
        break;
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return;

    // Validate required fields
    if (!documentMetadata.title || !documentMetadata.type || !documentMetadata.expirationDate) {
      setUploadError('Please fill in all required fields: Document Name, Type, and Expiration Date');
      return;
    }

    setUploadError(null);
    
    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const baseTitle = documentMetadata.title || file.name.split('.')[0];
        const finalTitle = selectedFiles.length > 1 ? `${baseTitle} (${index + 1})` : baseTitle;
        
        const metadata = {
          ...documentMetadata,
          title: finalTitle,
          category: 'compliance',
          entityType: documentMetadata.entityType || 'company',
          entityName: documentMetadata.entityName || 'Fleet Operations',
          uploadedBy: 'Current User',
          tags: ['compliance', 'certification', documentMetadata.type],
          issueDate: documentMetadata.issueDate || new Date().toISOString()
        };

        return await documentStorageService.uploadDocument(file, metadata, {
          enableOCR: true,
          onProgress: (progress) => setUploadProgress(progress)
        });
      });

      const results = await Promise.all(uploadPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        console.log(`Successfully uploaded ${successful.length} documents`);
        setSelectedFiles([]);
        setShowUploadModal(false);
        setUploadProgress(0);
        // Reset metadata form
        setDocumentMetadata({
          title: '',
          type: '',
          category: '',
          entityType: 'company',
          entityName: '',
          expirationDate: '',
          issueDate: '',
          documentNumber: '',
          issuingAuthority: '',
          notes: ''
        });
        
        // Trigger page refresh or callback
        if (onUpload) onUpload({ success: true, count: successful.length });
      }

      if (failed.length > 0) {
        setUploadError(`Failed to upload ${failed.length} files`);
      }
    } catch (error) {
      setUploadError('Upload failed: ' + error.message);
    }
  };

  const handleStartScan = async () => {
    try {
      const scanResult = await documentStorageService.scanDocument({
        facingMode: 'environment',
        width: 1920,
        height: 1080
      });

      if (scanResult.success) {
        // Handle scan stream setup - would need additional UI component
        console.log('Scan started successfully');
      } else {
        setUploadError('Failed to start scanning: ' + scanResult.error);
      }
    } catch (error) {
      setUploadError('Scan error: ' + error.message);
    }
  };

  const handleDownloadDocument = async (document) => {
    try {
      const result = await documentStorageService.downloadDocument(document.id);
      if (!result.success) {
        setUploadError('Download failed: ' + result.error);
      }
    } catch (error) {
      setUploadError('Download error: ' + error.message);
    }
  };


  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 sm:py-8">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                      <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Compliance Center</h1>
                    <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2 hidden sm:block">Monitor and manage all compliance documents and certifications</p>
                    <p className="text-xs text-gray-600 mt-1 sm:hidden">Manage compliance documents</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row md:mt-0 md:ml-4 space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => navigate('/documents')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <List className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">View All Documents</span>
                  <span className="sm:hidden">Documents</span>
                </button>
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    disabled={isLoading}
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">Refresh Data</span>
                    <span className="sm:hidden">Refresh</span>
                  </button>
                )}
                <button
                  onClick={() => handleAction('upload')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent rounded-lg text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <Upload className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Upload Document</span>
                  <span className="sm:hidden">Upload</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {(error || statusMessage || isLoading) && (
          <div className="mb-6 space-y-3">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {!error && statusMessage && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {statusMessage}
              </div>
            )}
            {isLoading && (
              <div className="flex items-center space-x-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Syncing compliance data...</span>
              </div>
            )}
          </div>
        )}

        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {/* Compliance Rate */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 lg:p-6 col-span-2 lg:col-span-1">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                  <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Compliance Rate</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{overviewMetrics.complianceRate}%</p>
                <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2 mt-1 sm:mt-2">
                  <div 
                    className="bg-green-600 h-1.5 sm:h-2 rounded-full transition-all duration-500"
                    style={{ width: `${overviewMetrics.complianceRate}%` }}
                  ></div>
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] sm:text-xs text-gray-500">
                  <span className="capitalize">Level: {overviewMetrics.level}</span>
                  <span>
                    {overviewMetrics.compliantItems ?? 0}
                    <span className="mx-1 text-gray-400">/</span>
                    {overviewMetrics.totalItems ?? 0} compliant
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Total Documents */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Total</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{overviewMetrics.total}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1 sm:mt-0 sm:ml-auto hidden sm:block">All categories</p>
            </div>
          </div>

          {/* Expired Documents */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
                    <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-red-600" />
                  </div>
                </div>
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Expired</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600">{overviewMetrics.expired}</p>
                </div>
              </div>
              <p className="text-xs text-red-500 mt-1 sm:mt-0 sm:ml-auto hidden sm:block">Need renewal</p>
            </div>
          </div>

          {/* Expiring Soon */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-yellow-600" />
                  </div>
                </div>
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Expiring</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-yellow-600">{overviewMetrics.expiringSoon}</p>
                </div>
              </div>
              <p className="text-xs text-yellow-600 mt-1 sm:mt-0 sm:ml-auto hidden sm:block">30 days</p>
            </div>
          </div>
        </div>

        {!isLoading && processedDocuments.length === 0 && (
          <div className="mb-6 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-6 text-center text-sm text-gray-600 sm:px-6">
            No compliance documents found for your company yet. Use the upload button to add required records and build your compliance profile.
          </div>
        )}

        {/* Critical Alerts Banner */}
        {(overviewMetrics.expired > 0 || overviewMetrics.critical > 0) && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center">
              <div className="flex items-center flex-1">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
                </div>
                <div className="ml-3 sm:ml-4 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-red-900">Immediate Action Required</h3>
                  <p className="text-sm sm:text-base text-red-700 mt-1">
                    {overviewMetrics.expired > 0 && `${overviewMetrics.expired} document(s) expired`}
                    {overviewMetrics.expired > 0 && overviewMetrics.expiringSoon > 0 && ', '}
                    {overviewMetrics.expiringSoon > 0 && `${overviewMetrics.expiringSoon} expiring soon`}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-3 sm:mt-0">
                <button
                  onClick={() => navigate('/documents?filter=expired')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-red-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  View Expired
                </button>
                <button
                  onClick={() => navigate('/documents?filter=expiring')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-orange-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Review Expiring
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Timeline */}
        <ComplianceTimeline
          className="mb-8"
          events={timelineEvents}
          isLoading={isLoading}
        />

      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowUploadModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Upload className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Upload Compliance Documents</h3>
                    <div className="mt-4 space-y-4">
                      {/* Document Metadata Form */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={documentMetadata.title}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g., DOT Medical Certificate"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={documentMetadata.type}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, type: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Select Type</option>
                            <option value="license">License</option>
                            <option value="medical">Medical Certificate</option>
                            <option value="insurance">Insurance</option>
                            <option value="registration">Registration</option>
                            <option value="inspection">Inspection</option>
                            <option value="certification">Certification</option>
                            <option value="training">Training Certificate</option>
                            <option value="permit">Permit</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Entity Type
                          </label>
                          <select
                            value={documentMetadata.entityType}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, entityType: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="company">Company</option>
                            <option value="driver">Driver</option>
                            <option value="vehicle">Vehicle</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Entity Name
                          </label>
                          <input
                            type="text"
                            value={documentMetadata.entityName}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, entityName: e.target.value }))}
                            placeholder="e.g., John Smith, Truck TR-001"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiration Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={documentMetadata.expirationDate}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, expirationDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Issue Date
                          </label>
                          <input
                            type="date"
                            value={documentMetadata.issueDate}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, issueDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document Number
                          </label>
                          <input
                            type="text"
                            value={documentMetadata.documentNumber}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, documentNumber: e.target.value }))}
                            placeholder="e.g., CDL-123456789"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Issuing Authority
                          </label>
                          <input
                            type="text"
                            value={documentMetadata.issuingAuthority}
                            onChange={(e) => setDocumentMetadata(prev => ({ ...prev, issuingAuthority: e.target.value }))}
                            placeholder="e.g., State DMV, DOT"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notes
                        </label>
                        <textarea
                          value={documentMetadata.notes}
                          onChange={(e) => setDocumentMetadata(prev => ({ ...prev, notes: e.target.value }))}
                          rows={2}
                          placeholder="Additional notes or comments..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* File Upload Section */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-blue-600 hover:text-blue-500 font-medium"
                          >
                            Select files to upload
                          </button>
                          <p className="text-gray-500 text-sm mt-1">PDF, Images, Documents up to 50MB</p>
                        </div>
                      </div>
                      
                      {selectedFiles.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-900">Selected Files:</h4>
                          <ul className="mt-2 space-y-1">
                            {selectedFiles.map((file, index) => (
                              <li key={index} className="text-sm text-gray-600 flex items-center justify-between">
                                <span>{file.name}</span>
                                <span className="text-xs text-gray-400">{documentStorageService.formatFileSize(file.size)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {uploadProgress > 0 && (
                        <div className="mt-4">
                          <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Uploading...</span>
                            <span>{uploadProgress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                          </div>
                        </div>
                      )}
                      
                      {uploadError && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-600">{uploadError}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleFileUpload}
                  disabled={selectedFiles.length === 0 || uploadProgress > 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadProgress > 0 ? 'Uploading...' : 'Upload Documents'}
                </button>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFiles([]);
                    setUploadProgress(0);
                    setUploadError(null);
                    setDocumentMetadata({
                      title: '',
                      type: '',
                      category: '',
                      entityType: 'company',
                      entityName: '',
                      expirationDate: '',
                      issueDate: '',
                      documentNumber: '',
                      issuingAuthority: '',
                      notes: ''
                    });
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OCR Result Modal */}
      {ocrResult && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setOcrResult(null)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">OCR Extraction Results</h3>
                  <button onClick={() => setOcrResult(null)}>
                    <X className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Extracted Text:</h4>
                    <div className="bg-gray-50 p-3 rounded-md max-h-40 overflow-y-auto">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{ocrResult.extractedText}</p>
                    </div>
                  </div>
                  
                  {ocrResult.extractedFields && Object.keys(ocrResult.extractedFields).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Extracted Fields:</h4>
                      <div className="space-y-2">
                        {Object.entries(ocrResult.extractedFields).map(([key, value]) => (
                          <div key={key} className="flex justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm font-medium text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                            <span className="text-sm text-gray-900">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
                    <span className="text-sm font-medium text-blue-900">Confidence Score:</span>
                    <span className="text-sm text-blue-700">{ocrResult.confidence}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDashboard;
